// ═══════════════════════════════════════════════════════════════════
//  IDIOMA Y MONEDA (propio, sin Google Translate)
//  - El texto se escribe en español en el código; t("…") lo cambia al inglés con el diccionario de en.js.
//    Si una frase no está en el diccionario, se queda en español (así nunca sale una página rota).
//  - El idioma y la moneda se detectan por el navegador y la zona horaria, y el visitante los cambia con un clic.
//  - Los precios se calculan siempre en pesos (MXN). En otra moneda se muestran aproximados con el tipo de cambio
//    del día (Frankfurter, datos del Banco Central Europeo, gratis y sin llave); se cobra siempre en MXN.
// ═══════════════════════════════════════════════════════════════════
import { EN } from "./en";

export const IDIOMAS = [["es", "Español"], ["en", "English"]];
export const MONEDAS = ["MXN", "USD", "EUR", "CAD", "GBP"];
// Respaldo por si no hay internet o el servicio no contesta (MXN → moneda, 19 oct 2026)
const RESPALDO = { MXN: 1, USD: 0.0541, EUR: 0.0482, CAD: 0.07614, GBP: 0.04058 };

// Buscadores y lectores de páginas (Google, Bing, las IA, vistas previas de WhatsApp): ven siempre la versión base,
// español y pesos, sin adivinar por su idioma o zona horaria (Google lee la página desde EE. UU. y en inglés).
const esRobot = () => { try { return /bot|crawl|spider|slurp|lighthouse|facebookexternalhit|embedly|preview|whatsapp|telegram|vkshare|pinterest|quora/i.test(navigator.userAgent || ""); } catch { return false; } };
const guardado = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const guardar = (k, v) => { try { localStorage.setItem(k, v); } catch {} };

// /en/ es la versión en inglés (la que Google enseña en inglés); / es la de español
const rutaIngles = () => { try { return /^\/en(\/|$)/.test(window.location.pathname); } catch { return false; } };
const rutaPortada = () => { try { return /^\/(en\/?)?$/.test(window.location.pathname); } catch { return false; } };

function idiomaInicial() {
  if (rutaIngles()) return "en";
  const g = guardado("ns-idioma");
  if (g === "es" || g === "en") return g;
  try {
    const q = new URLSearchParams(window.location.search).get("lang");
    if (q === "es" || q === "en") return q;
    if (esRobot()) return "es";
    const l = (navigator.languages || [navigator.language || "es"]).map((x) => String(x).toLowerCase());
    return l[0].startsWith("es") ? "es" : "en";
  } catch { return "es"; }
}

function monedaInicial() {
  const g = guardado("ns-moneda");
  if (MONEDAS.includes(g)) return g;
  try {
    if (esRobot()) return "MXN";
    const zona = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (/^America\/(Mexico_City|Cancun|Merida|Monterrey|Matamoros|Chihuahua|Ojinaga|Mazatlan|Hermosillo|Tijuana|Bahia_Banderas|Ciudad_Juarez)$/.test(zona)) return "MXN";
    const region = ((navigator.language || "").split("-")[1] || "").toUpperCase();
    if (region === "MX") return "MXN";
    if (region === "CA" || /^America\/(Toronto|Vancouver|Edmonton|Winnipeg|Halifax|Regina|St_Johns)$/.test(zona)) return "CAD";
    if (region === "GB" || zona === "Europe/London") return "GBP";
    if (zona.startsWith("Europe/") || ["ES", "FR", "DE", "IT", "PT", "NL", "BE", "AT", "IE", "FI", "GR"].includes(region)) return "EUR";
    return "USD";
  } catch { return "MXN"; }
}

let idioma = typeof window === "undefined" ? "es" : idiomaInicial();
let moneda = typeof window === "undefined" ? "MXN" : monedaInicial();
let tasas = { ...RESPALDO };
const avisar = () => window.dispatchEvent(new Event("ns-idioma"));

export const getIdioma = () => idioma;
export const getMoneda = () => moneda;
export const enIngles = () => idioma === "en";

export function setIdioma(v) {
  idioma = v === "en" ? "en" : "es";
  guardar("ns-idioma", idioma);
  try { document.documentElement.lang = idioma === "en" ? "en" : "es-MX"; } catch {}
  // la dirección sigue al idioma (/ ↔ /en/), así se puede compartir la liga en el idioma que se está viendo
  try { if (rutaPortada()) window.history.replaceState(window.history.state, "", (idioma === "en" ? "/en/" : "/") + window.location.search + window.location.hash); } catch {}
  avisar();
}
export function setMoneda(v) {
  if (!MONEDAS.includes(v)) return;
  moneda = v; guardar("ns-moneda", v); avisar();
}

// Tipo de cambio del día (una vez al día, guardado en el navegador)
export async function cargarTasas() {
  try {
    const c = JSON.parse(guardado("ns-tasas") || "null");
    const hoy = new Date().toISOString().slice(0, 10);
    if (c && c.dia === hoy && c.rates) { tasas = { MXN: 1, ...c.rates }; avisar(); return; }
    const r = await fetch("https://api.frankfurter.dev/v1/latest?base=MXN&symbols=USD,EUR,CAD,GBP");
    const j = await r.json();
    if (j?.rates?.USD) { tasas = { MXN: 1, ...j.rates }; guardar("ns-tasas", JSON.stringify({ dia: hoy, rates: j.rates })); avisar(); }
  } catch { /* se queda el respaldo */ }
}

// Precio en la moneda elegida (el número de entrada siempre es en pesos)
export function dinero(mxn) {
  const v = (Number(mxn) || 0) * (tasas[moneda] || RESPALDO[moneda] || 1);
  try {
    return new Intl.NumberFormat(idioma === "en" ? "en-US" : "es-MX", { style: "currency", currency: moneda, maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(Math.round(v));
  } catch { return `${moneda} ${Math.round(v).toLocaleString()}`; }
}
export const enPesos = () => moneda === "MXN";

// Texto: en inglés si existe en el diccionario. {x} se reemplaza con vars.x
export function t(s, vars) {
  let r = idioma === "en" && EN[s] ? EN[s] : s;
  if (vars) for (const k of Object.keys(vars)) r = r.split(`{${k}}`).join(vars[k]);
  return r;
}

// Renglones del desglose que el cotizador arma con números adentro
const PATRONES = [
  [/^Render en máquina \((\d+) s a 24 cuadros\)$/, (m) => `Machine render (${m[1]} s at 24 fps)`],
  [/^Retoque de (\d+) fotos$/, (m) => `Retouching ${m[1]} photos`],
  [/^(\d+) reels al mes$/, (m) => `${m[1]} reels a month`],
  [/^(\d+) versión o formato extra$/, (m) => `${m[1]} extra version or format`],
  [/^(\d+) versiones o formatos extra$/, (m) => `${m[1]} extra versions or formats`],
  [/^Derechos de uso: (.+), (.+)$/, (m) => `Usage rights: ${t(m[1])}, ${t(m[2])}`],
  [/^Entrega (urgente|prioritaria) \((.+)\)$/, (m) => `${m[1] === "urgente" ? "Rush" : "Priority"} delivery (${t(m[2])})`],
];
export function tl(s) {
  if (idioma !== "en") return s;
  if (EN[s]) return EN[s];
  for (const [re, f] of PATRONES) { const m = s.match(re); if (m) return f(m); }
  return s;
}
